import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Button from '../../ui/Button'
import SearchInput from '../../ui/SearchInput'
import EmptyState from '../../ui/EmptyState'
import CenteredState from '../../ui/CenteredState'
import Toast from '../../shared/Toast'
import Loader from '../../shared/Loader'
import StatsCardsGroup from './StatsCardsGroup'
import StatusFilter from './StatusFilter'
import ContentFilter from './ContentFilter'
import CollectsFilter from './CollectsFilter'
import TriggeredAlertsTable from './TriggeredAlertsTable'
import StatusInlineEditor from './StatusInlineEditor'
import AttributionInlineEditor from './AttributionInlineEditor'
import EditIssueDrawer from './EditIssueDrawer'
import PhotoViewer, { buildViewerPhotos } from '../../ui/PhotoViewer'
import ShareIssueModal from './ShareIssueModal'
import ColumnSettingsModal from './ColumnSettingsModal'
import ExportMenu from './ExportMenu'
import SelectionToolbar from './SelectionToolbar'
import BatchStatusModal from './BatchStatusModal'
import issueExportUrl from './issueExportUrl'
import useTriggeredAlerts from '../hooks/useTriggeredAlerts'
import useIssuesByStatus from '../hooks/useIssuesByStatus'
import useIssueStatuses from '../hooks/useIssueStatuses'
import useIssueMutations from '../hooks/useIssueMutations'
import useColumnPreferences from '../hooks/useColumnPreferences'
import useLazyList from '../hooks/useLazyList'
import useOrganizationAlerts from '../hooks/useOrganizationAlerts'
import useEditorLazyData from '../hooks/useEditorLazyData'
import useIssueDrawerUrl from '../hooks/useIssueDrawerUrl'
import {
  CONTENT_FILTER_OPTIONS, DEFAULT_COLUMN_PREFERENCES, EXTRA_COLUMN_TYPES,
  ORGANIZATION_PRIMARY_COLUMN_ORDER, PRIMARY_COLUMN_KEYS, PRIMARY_COLUMN_ORDER, TOAST_CONTAINER_ID
} from './constants'
import IssuesService from '../../../services/api/IssuesService'
import CollectService from '../../../services/api/CollectService'
import OrganizationIssuesService from '../../../services/api/OrganizationIssuesService'
import { updateUrlFilters, useUrlFilters } from '../../../utils/urlFilters'

const extractQuestionSections = response => response?.sections || []
const extractVariables = response => response?.variables || []

// The list payload carries the photo urls, so the viewer opens without another request.
const PHOTO_ALT = 'Photo de l’alerte'
const EMPTY_PHOTOS = []
// Stable fetcher refs for the column picker; org aggregates across collects.
const columnListFetchers = isOrganization => ({
  questions: isOrganization ? OrganizationIssuesService.getQuestions : CollectService.getQuestions,
  variables: isOrganization ? OrganizationIssuesService.getVariables : CollectService.getVariables
})

const ISSUE_STATUS_PARAM = 'issue_status'
const CONTENT_PARAM = 'issue_content'
const COLLECT_IDS_PARAM = 'collect_ids'
const VALID_CONTENT = CONTENT_FILTER_OPTIONS.map(option => option.value)
const STATUS_ID_RE = /^\d+$/u

const parseCsvParam = (param, allowed) => {
  const raw = new URLSearchParams(window.location.search).get(param)
  if (!raw)
    return []
  return raw.split(',').filter(value => allowed.includes(value))
}

// Per-collect tokens are status ids loaded async, so drop stale legacy ones; org tokens are names.
const initialStatusValues = isOrganization => {
  const raw = new URLSearchParams(window.location.search).get(ISSUE_STATUS_PARAM)
  if (!raw)
    return []
  const tokens = raw.split(',').map(token => token.trim()).filter(Boolean)
  return isOrganization ? tokens : tokens.filter(token => STATUS_ID_RE.test(token))
}
const initialContentValues = () => parseCsvParam(CONTENT_PARAM, VALID_CONTENT)
const initialCollectIds = () => {
  const raw = new URLSearchParams(window.location.search).get(COLLECT_IDS_PARAM)
  if (!raw)
    return []
  return raw.split(',').map(Number).filter(Number.isInteger)
}

const csvOrNull = values => values.length > 0 ? values.join(',') : null

const DEFAULT_PER_PAGE = 10
const SEARCH_DEBOUNCE_MS = 300
const EMPTY_TITLE = 'Aucune donnée'
const EMPTY_MESSAGE = 'Aucune alerte pour le moment. Configurez vos critères pour être '
  + 'notifié automatiquement lors de réponses sensibles.'
const ALERTS_HEADER_CLASS = 'tw:flex tw:flex-col tw:gap-3 tw:lg:flex-row '
  + 'tw:lg:items-center tw:lg:justify-between'
const ALERTS_HEADER_TITLE_CLASS = 'tw:m-0! tw:text-ds-neutral-highest tw:text-lg! '
  + 'tw:font-medium! tw:leading-tight'
const FILTER_FIELD_CLASS = 'tw:w-52'
const FULL_NAME_KEY = 'full_name'
const STATUS_ID_KEY = 'status_id'
const STATUS_NAME_KEY = 'status_name'
const STATUS_COLOR_KEY = 'status_color'
const STATUS_CATEGORY_KEY = 'status_category'
const EMPTY_COLLECTS = []
const EMPTY_SELECTION = new Set()

// An empty selection means the whole filtered scope, not none (cf. issueExportUrl).
const exportScope = (allFiltered, selectedIds) => allFiltered ? EMPTY_SELECTION : selectedIds

const applyStatus = (issue, issueStatus) => ({
  ...issue,
  [STATUS_ID_KEY]: issueStatus.id,
  [STATUS_NAME_KEY]: issueStatus.name,
  [STATUS_COLOR_KEY]: issueStatus.color,
  [STATUS_CATEGORY_KEY]: issueStatus.category
})

const applyAttribution = (issue, userIds, allUsers) => {
  const lookup = new Map(allUsers.map(user => [user.id, user]))
  const previous = new Map(issue.attribution.map(user => [user.id, user]))
  const attribution = userIds.map(id => lookup.get(id) || previous.get(id)).filter(Boolean)
  return { ...issue, attribution }
}

const noop = () => null

const renderFullPageState = ({
  loading, loaded, error, issues, search, statusValues, contentValues,
  collectIds, startDate, endDate, configureUrl
}) => {
  if (loading && !loaded) {
    return (
      <div>
        <Toast containerId={TOAST_CONTAINER_ID} />
        <CenteredState><Loader /></CenteredState>
      </div>
    )
  }
  const hasActiveFilters = Boolean(search) || statusValues.length > 0
    || contentValues.length > 0 || collectIds.length > 0 || Boolean(startDate) || Boolean(endDate)
  if (loading || error || issues.length > 0 || hasActiveFilters)
    return null

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <CenteredState>
        <EmptyState
          action={configureUrl ? (
            <Button
              className="tw:text-gray-900! tw:border-gray-200!"
              href={configureUrl}
              icon="gear"
              variant="neutral"
            >
              Configurer
            </Button>
          ) : null}
          message={EMPTY_MESSAGE}
          title={EMPTY_TITLE}
        />
      </CenteredState>
    </div>
  )
}

const pickCollects = (isOrganization, scope) =>
  isOrganization ? scope.collects || EMPTY_COLLECTS : EMPTY_COLLECTS

// Org has no single collect, resolving one per row from the edited issue, so pass null there.
const whenOrganization = (isOrganization, value) => isOrganization ? value : null
const whenCollect = (isOrganization, value) => isOrganization ? null : value

const scopedCollectId = (isOrganization, scopedId, fallback) => isOrganization ? scopedId ?? 0 : fallback

const mutationTarget = (isOrganization, editingIssue, fallback) =>
  isOrganization ? editingIssue?.collect_id ?? null : fallback

const buildConfigureUrl = ({ isOrganization, isAdmin, draftId, collectId }) =>
  !isOrganization && isAdmin && !draftId ? `/collects/${collectId}/alerts` : null

// One ternary hides the two data shapes (org: one endpoint; per-collect: three)
// Behind a single flat interface the tab renders from.
const selectScopedData = ({
  isOrganization, orgData, perCollect, perCollectStats, perCollectStatuses, availableUsers, lazyEditor
}) => {
  if (isOrganization) {
    return {
      issues: orgData.issues,
      pagy: orgData.pagy,
      tableLoading: orgData.loading,
      tableLoaded: orgData.hasLoaded,
      tableError: orgData.error,
      refetchTable: orgData.refetch,
      refetchStats: orgData.refetch,
      mergeIssue: orgData.mergeIssue,
      groups: orgData.groups,
      statsLoading: orgData.loading,
      issueStatuses: orgData.steps,
      editorIssueStatuses: lazyEditor.currentIssueStatuses,
      editorAvailableUsers: lazyEditor.currentAvailableUsers
    }
  }
  return {
    issues: perCollect.issues,
    pagy: perCollect.pagy,
    tableLoading: perCollect.loading,
    tableLoaded: perCollect.hasLoaded,
    tableError: perCollect.error,
    refetchTable: perCollect.refetch,
    refetchStats: perCollectStats.refetch,
    mergeIssue: perCollect.mergeIssue,
    groups: perCollectStats.groups,
    statsLoading: perCollectStats.loading,
    issueStatuses: perCollectStatuses.statuses,
    editorIssueStatuses: perCollectStatuses.statuses,
    editorAvailableUsers: availableUsers
  }
}

// Org row inserts the collect picker ahead of the two the per-collect row shows.
const renderFilters = ({
  isOrganization, collects, collectIds, onCollectsChange,
  issueStatuses, statusValues, onStatusChange,
  contentValues, onContentChange
}) => {
  const collectsFilter = (
    <div className={FILTER_FIELD_CLASS}>
      <CollectsFilter collects={collects} value={collectIds} onChange={onCollectsChange} />
    </div>
  )
  const contentFilter = (
    <div className={FILTER_FIELD_CLASS}>
      <ContentFilter value={contentValues} onChange={onContentChange} />
    </div>
  )
  const statusFilter = (
    <div className={FILTER_FIELD_CLASS}>
      <StatusFilter statuses={issueStatuses} value={statusValues} onChange={onStatusChange} />
    </div>
  )
  if (isOrganization) {
    return (
      <div className="tw:flex tw:items-center tw:gap-3">
        {collectsFilter}
        {statusFilter}
        {contentFilter}
      </div>
    )
  }
  return (
    <div className="tw:flex tw:items-center tw:gap-3">
      {contentFilter}
      {statusFilter}
    </div>
  )
}

const TriggeredAlertsTab = ({
  collectId = null, draftId = null, initialColumnPreferences = null, isAdmin = false, scope = null
}) => {
  const isOrganization = scope?.kind === 'organization'
  const collects = pickCollects(isOrganization, scope)

  const [page, setPage] = useState(1)
  const [items, setItems] = useState(DEFAULT_PER_PAGE)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusValues, setStatusValues] = useState(() => initialStatusValues(isOrganization))
  const [contentValues, setContentValues] = useState(initialContentValues)
  const [collectIds, setCollectIds] = useState(initialCollectIds)
  const [sort, setSort] = useState(null)
  const [direction, setDirection] = useState('desc')
  // Optimistic overrides keyed by issueId. Cleared once the server confirms.
  const [optimisticOverrides, setOptimisticOverrides] = useState({})
  const [editorMode, setEditorMode] = useState(null)
  const [editingIssue, setEditingIssue] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [availableUsers, setAvailableUsers] = useState([])
  const [editingIssueId, setEditingIssueId] = useState(null)
  const [editingIssueCollectId, setEditingIssueCollectId] = useState(null)
  const [restoredIssue, setRestoredIssue] = useState(null)
  const [sharingIssueId, setSharingIssueId] = useState(null)
  // Photos of the issue shown full screen, empty when the viewer is closed.
  const [viewerPhotos, setViewerPhotos] = useState(EMPTY_PHOTOS)
  const [sharingIssueCollectId, setSharingIssueCollectId] = useState(null)
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false)
  const [isBatchStatusOpen, setIsBatchStatusOpen] = useState(false)
  // Eager: titles must resolve in the table even when the settings modal is closed.
  // Owning the lists here and passing them as props avoids a second round-trip on open.
  // Org aggregates the lists across the authorized collects via its own endpoints.
  const columnFetchers = columnListFetchers(isOrganization)
  const {
    items: questionSections,
    loading: loadingQuestions
  } = useLazyList({
    collectId,
    isOpen: isColumnSettingsOpen,
    fetcher: columnFetchers.questions,
    extractor: extractQuestionSections,
    eager: true
  })
  const {
    items: extraVariables,
    loading: loadingVariables
  } = useLazyList({
    collectId,
    isOpen: isColumnSettingsOpen,
    fetcher: columnFetchers.variables,
    extractor: extractVariables,
    eager: true
  })
  const optionsLoading = loadingQuestions || loadingVariables
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [allFiltered, setAllFiltered] = useState(false)
  // Snapshots used to rollback an optimistic update if the PATCH fails.
  const snapshotsRef = useRef({})

  // Org preferences persist to /issues/preferences.json instead of a collect-scoped path.
  const preferencesSaver = isOrganization ? OrganizationIssuesService.savePreferences : null
  const {
    preferences: columnPreferences,
    setPreferences: setColumnPreferences,
    reset: resetColumnPreferences
  } = useColumnPreferences(collectId, DEFAULT_COLUMN_PREFERENCES, {
    initial: initialColumnPreferences,
    save: preferencesSaver
  })

  const urlFilters = useUrlFilters()
  const startDate = urlFilters.start_date
  const endDate = urlFilters.end_date

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  const statusParam = csvOrNull(statusValues)
  const contentParam = csvOrNull(contentValues)
  const collectIdsKey = collectIds.join(',')

  useEffect(() => {
    setPage(1)
    setSelectedIds(new Set())
    setAllFiltered(false)
  }, [
    debouncedSearch,
    statusParam,
    contentParam,
    collectIdsKey,
    items,
    sort,
    direction,
    startDate,
    endDate
  ])

  const handleToggleRow = useCallback((issueId, visibleIds = []) => {
    setSelectedIds(previous => {
      // Dropping the filtered scope keeps what the page shows, since the rest was never loaded.
      const next = allFiltered ? new Set(visibleIds) : new Set(previous)
      if (next.has(issueId))
        next.delete(issueId)
      else
        next.add(issueId)
      return next
    })
    setAllFiltered(false)
  }, [allFiltered])

  const handleToggleAll = useCallback(visibleIds => {
    setSelectedIds(previous => {
      const base = allFiltered ? new Set(visibleIds) : previous
      // Select-all behaviour: clear only when everything is already checked.
      // From an indeterminate state, complete the selection rather than wipe it.
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => base.has(id))
      const next = new Set(base)
      if (allSelected)
        visibleIds.forEach(id => next.delete(id))
      else
        visibleIds.forEach(id => next.add(id))
      return next
    })
    setAllFiltered(false)
  }, [allFiltered])

  // Per-collect data path: table + stats + configured statuses via three endpoints.
  const perCollect = useTriggeredAlerts(collectId, {
    page,
    items,
    search: debouncedSearch,
    issueStatus: statusParam,
    issueContent: contentParam,
    sort,
    direction,
    draftId,
    columns: columnPreferences.questionIds,
    variables: columnPreferences.variableIds,
    startDate,
    endDate,
    enabled: !isOrganization
  })
  // Stats follow the same search + date range as the table so their counts stay in sync;
  // Clicking a status card filters only the table (counts stay full for navigation).
  const perCollectStats = useIssuesByStatus(collectId, {
    search: debouncedSearch, draftId, startDate, endDate, enabled: !isOrganization
  })
  // Configured workflow steps for the collect — feed the filter and the inline editor.
  const perCollectStatuses = useIssueStatuses(whenCollect(isOrganization, collectId))
  // Org data path: one GET /issues.json yields issues + stats + steps + pagy.
  const orgData = useOrganizationAlerts({
    page,
    items,
    search: debouncedSearch,
    collectIds,
    steps: statusValues,
    content: contentValues,
    sort,
    direction,
    columns: columnPreferences.questionIds,
    variables: columnPreferences.variableIds,
    startDate,
    endDate,
    enabled: isOrganization
  })

  // Inline editors mutate through the issue's own collect, which org lazily loads per edited row.
  // StatusFilter reads the org union of steps (id === name) while the editor reads that collect's ids.
  const lazyEditor = useEditorLazyData(
    whenOrganization(isOrganization, editorMode), whenOrganization(isOrganization, editingIssue)
  )
  const {
    issues, pagy, tableLoading, tableLoaded, tableError, refetchTable, refetchStats,
    mergeIssue, groups, statsLoading, issueStatuses, editorIssueStatuses, editorAvailableUsers
  } = selectScopedData({
    isOrganization, orgData, perCollect, perCollectStats, perCollectStatuses, availableUsers, lazyEditor
  })

  const handleIssueUpdated = useCallback(() => {
    refetchTable()
    refetchStats()
  }, [refetchTable, refetchStats])

  // Optimistic overrides are reconciled per row in handleMutationSuccess/Error.
  // A global reset would wipe a row whose edit is still in flight when another row reconciles.

  useEffect(() => {
    if (isOrganization || editorMode !== 'attribution' || availableUsers.length > 0)
      return noop
    let cancelled = false
    IssuesService.getAssignableUsers(collectId).then(response => {
      if (cancelled)
        return
      const users = (response || []).map(option => ({
        id: option.user_id,
        [FULL_NAME_KEY]: option.user_name
      }))
      setAvailableUsers(users)
    })
    return () => {
      cancelled = true
    }
  }, [isOrganization, editorMode, availableUsers.length, collectId])

  const clearOverride = useCallback(issueId => {
    setOptimisticOverrides(previous => {
      if (!previous[issueId])
        return previous
      const next = { ...previous }
      delete next[issueId]
      return next
    })
    delete snapshotsRef.current[issueId]
  }, [])

  const handleMutationSuccess = useCallback(({ issueId, issueStatus, userIds, optimistic, issue }) => {
    if (!optimistic) {
      // One refetch keeps the org-only fields (collect_id/collect_name/since) the PATCH drops.
      if (isOrganization) {
        clearOverride(issueId)
        refetchTable()
        return
      }
      refetchStats()
      // No canonical payload: drop the override and refetch, or a stale one lingers over fresh data.
      if (!issue) {
        clearOverride(issueId)
        refetchTable()
        return
      }
      // Surgical reconcile: replace just the mutated row rather than refetch the whole table.
      mergeIssue(issue)
      clearOverride(issueId)
      return
    }
    const target = issues.find(currentIssue => currentIssue.id === issueId)
    if (!target)
      return
    if (!snapshotsRef.current[issueId])
      snapshotsRef.current[issueId] = target
    setOptimisticOverrides(previous => {
      let patched = previous[issueId] || target
      if (issueStatus)
        patched = applyStatus(patched, issueStatus)
      if (userIds)
        patched = applyAttribution(patched, userIds, editorAvailableUsers)
      return { ...previous, [issueId]: patched }
    })
  }, [issues, editorAvailableUsers, isOrganization, refetchStats, mergeIssue, refetchTable, clearOverride])

  const handleMutationError = useCallback(({ issueId }) => {
    clearOverride(issueId)
  }, [clearOverride])

  const displayedIssues = useMemo(() => {
    if (Object.keys(optimisticOverrides).length === 0)
      return issues
    return issues.map(issue => optimisticOverrides[issue.id] || issue)
  }, [issues, optimisticOverrides])

  // Org targets the edited issue's own collect; per-collect always uses the page collect.
  const mutationCollectId = mutationTarget(isOrganization, editingIssue, collectId)
  const { updateStatus, updateAttribution, isUpdating } = useIssueMutations(mutationCollectId, {
    onSuccess: handleMutationSuccess,
    onError: handleMutationError,
    toastContainerId: TOAST_CONTAINER_ID
  })

  // Org resolves the issue through the collect carried in the URL; per-collect uses its page one.
  const loadRestoredIssue = useCallback((issueId, urlCollectId) => {
    const targetCollectId = isOrganization ? urlCollectId : collectId
    return IssuesService.getIssue(targetCollectId, issueId).catch(() => null)
  }, [isOrganization, collectId])

  const handleIssueRestore = useCallback((issueId, urlCollectId, issue) => {
    setRestoredIssue(issue)
    setEditingIssueId(issueId)
    setEditingIssueCollectId(isOrganization ? urlCollectId : null)
  }, [isOrganization])

  const { openIssue, closeIssue } = useIssueDrawerUrl({
    isOrganization,
    loadIssue: loadRestoredIssue,
    onRestore: handleIssueRestore
  })

  const handleSearchChange = useCallback(event => setSearchInput(event.target.value), [])
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setAllFiltered(false)
  }, [])
  const selectAllFiltered = useCallback(() => setAllFiltered(true), [])
  const handleBatchStatusOpen = useCallback(() => setIsBatchStatusOpen(true), [])
  const handleBatchStatusClose = useCallback(() => setIsBatchStatusOpen(false), [])
  const handleBatchStatusUpdated = useCallback(() => {
    setPage(1)
    clearSelection()
    handleIssueUpdated()
  }, [clearSelection, handleIssueUpdated])
  const batchStatusScope = useMemo(() => ({
    issueIds: [...selectedIds],
    allFiltered,
    draftId,
    search: debouncedSearch,
    issueStatus: statusParam,
    issueContent: contentParam,
    startDate,
    endDate
  }), [selectedIds, allFiltered, draftId, debouncedSearch, statusParam, contentParam, startDate, endDate])
  const exportedIds = exportScope(allFiltered, selectedIds)
  const handleStatusChange = useCallback(next => {
    setStatusValues(next)
    updateUrlFilters({ [ISSUE_STATUS_PARAM]: csvOrNull(next) })
  }, [])
  const handleContentChange = useCallback(next => {
    setContentValues(next)
    updateUrlFilters({ [CONTENT_PARAM]: csvOrNull(next) })
  }, [])
  const handleCollectsChange = useCallback(next => {
    setCollectIds(next)
    updateUrlFilters({ [COLLECT_IDS_PARAM]: next.length > 0 ? next.join(',') : null })
  }, [])
  // TanStack owns the asc → desc → none cycle, so storing its direction verbatim avoids a second one.
  const handleSortChange = useCallback((column, nextDirection) => {
    setSort(column)
    setDirection(nextDirection || 'desc')
  }, [])
  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true)
  }, [])
  const handleColumnSettingsClose = useCallback(() => {
    setIsColumnSettingsOpen(false)
  }, [])
  const handleColumnSettingsApply = useCallback(next => {
    setColumnPreferences(next)
  }, [setColumnPreferences])
  const handleColumnSettingsReset = useCallback(() => {
    resetColumnPreferences()
  }, [resetColumnPreferences])
  const handleIssueClick = useCallback(issue => {
    setEditingIssueId(issue.id)
    setEditingIssueCollectId(issue.collect_id ?? null)
    openIssue(issue.id, issue.collect_id ?? null)
  }, [openIssue])
  const handleShareClick = useCallback(issue => {
    setSharingIssueId(issue.id)
    setSharingIssueCollectId(issue.collect_id ?? null)
  }, [])
  const handlePhotosClick = useCallback(issue => {
    setViewerPhotos(buildViewerPhotos(issue.photos || EMPTY_PHOTOS, PHOTO_ALT))
  }, [])
  const handleViewerClose = useCallback(() => setViewerPhotos(EMPTY_PHOTOS), [])
  const buildExportUrl = useCallback((format, ids) => {
    if (isOrganization) {
      return OrganizationIssuesService.exportUrl({
        search: debouncedSearch,
        collectIds,
        steps: statusValues,
        content: contentValues,
        columns: columnPreferences.questionIds,
        variables: columnPreferences.variableIds,
        startDate,
        endDate,
        selectedIds: ids
      }, format)
    }
    return issueExportUrl(collectId, format, {
      draftId,
      search: debouncedSearch,
      issueStatus: statusParam,
      issueContent: contentParam,
      selectedIds: ids
    })
  }, [
    isOrganization,
    debouncedSearch,
    collectIds,
    statusValues,
    contentValues,
    columnPreferences.questionIds,
    columnPreferences.variableIds,
    startDate,
    endDate,
    collectId,
    draftId,
    statusParam,
    contentParam
  ])
  const handleExportPdf = useCallback(issue => {
    window.open(issue.pdf_url, '_blank', 'noopener,noreferrer')
  }, [])
  const handleEditClose = useCallback(() => {
    setRestoredIssue(null)
    setEditingIssueId(null)
    setEditingIssueCollectId(null)
    closeIssue()
  }, [closeIssue])
  const handleShareClose = useCallback(() => {
    setSharingIssueId(null)
    setSharingIssueCollectId(null)
  }, [])

  const handleEditorClose = useCallback(() => {
    setEditorMode(null)
    setEditingIssue(null)
    setAnchorEl(null)
  }, [])

  // Toggle: clicking the same cell again closes the editor; switching cells reopens.
  const toggleEditor = useCallback((mode, issue, element) => {
    if (editorMode === mode && editingIssue?.id === issue.id) {
      handleEditorClose()
      return
    }
    setEditingIssue(issue)
    setAnchorEl(element)
    setEditorMode(mode)
  }, [editorMode, editingIssue, handleEditorClose])

  const openStatusEditor = (issue, element) => toggleEditor('status', issue, element)
  const handleStatusEdit = useCallback(openStatusEditor, [toggleEditor])

  const openAttributionEditor = (issue, element) => toggleEditor('attribution', issue, element)
  const handleAttributionEdit = useCallback(openAttributionEditor, [toggleEditor])

  const handleStatusSelect = useCallback(value => {
    if (!editingIssue)
      return
    const issueStatus = editorIssueStatuses.find(status => String(status.id) === String(value))
    if (!issueStatus)
      return
    updateStatus(editingIssue.id, issueStatus.id, issueStatus)
  }, [editingIssue, editorIssueStatuses, updateStatus])

  const handleAttributionApply = useCallback(userIds => {
    if (!editingIssue)
      return
    updateAttribution(editingIssue.id, userIds)
  }, [editingIssue, updateAttribution])

  const currentUserIds = useMemo(() => {
    if (!editingIssue)
      return []
    return editingIssue.attribution.map(user => user.id)
  }, [editingIssue])

  // Each entry carries its type so the table routes to extra_values or extra_variable_values.
  const extraColumnsForTable = useMemo(() => {
    const allQuestions = questionSections.flatMap(section => section.questions)
    const questionLookup = new Map(allQuestions.map(question => [question.id, question]))
    // Org variables arrive grouped by ensemble; flatten to variables before the lookup.
    const allVariables = extraVariables.flatMap(entry => entry.variables || [entry])
    const variableLookup = new Map(allVariables.map(variable => [variable.id, variable]))
    const questions = columnPreferences.questionIds
      .map(id => questionLookup.get(id))
      .filter(Boolean)
      .map(question => ({ type: EXTRA_COLUMN_TYPES.QUESTION, id: question.id, title: question.title }))
    const variables = columnPreferences.variableIds
      .map(id => variableLookup.get(id))
      .filter(Boolean)
      .map(variable => ({ type: EXTRA_COLUMN_TYPES.VARIABLE, id: variable.id, title: variable.name }))
    return [...questions, ...variables]
  }, [
    columnPreferences.questionIds,
    columnPreferences.variableIds,
    questionSections,
    extraVariables
  ])

  // Org view prepends the collect column (absent from the per-collect preferences)
  // So a cross-collect row shows which collect the alert belongs to.
  const primaryColumnKeys = useMemo(() => {
    if (!isOrganization)
      return columnPreferences.primaryColumns
    return [PRIMARY_COLUMN_KEYS.COLLECT_NAME, ...columnPreferences.primaryColumns]
  }, [isOrganization, columnPreferences.primaryColumns])

  const visibleColumns = useMemo(() => ({
    primary: primaryColumnKeys,
    extra: extraColumnsForTable
  }), [primaryColumnKeys, extraColumnsForTable])

  const configureUrl = buildConfigureUrl({ isOrganization, isAdmin, draftId, collectId })
  const fullPageState = renderFullPageState({
    loading: tableLoading,
    loaded: tableLoaded,
    error: tableError,
    issues,
    search: debouncedSearch,
    statusValues,
    contentValues,
    collectIds,
    startDate,
    endDate,
    configureUrl
  })
  if (fullPageState)
    return fullPageState

  const filters = renderFilters({
    isOrganization,
    collects,
    collectIds,
    onCollectsChange: handleCollectsChange,
    issueStatuses,
    statusValues,
    onStatusChange: handleStatusChange,
    contentValues,
    onContentChange: handleContentChange
  })

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
          <div className="tw:w-full tw:max-w-[531px]">
            <SearchInput
              name="alerts-search"
              placeholder="Rechercher une alerte, attribution, ressource,..."
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
          <div className="tw:flex tw:items-center tw:gap-3">
            {configureUrl ? (
              <Button
                className="tw:text-gray-700! tw:hover:bg-gray-50!"
                href={configureUrl}
                icon="gear"
                variant="neutral"
              >
                Configuration
              </Button>
            ) : null}
            <ExportMenu
              buildExportUrl={buildExportUrl}
              selectedIds={exportedIds}
              toastContainerId={TOAST_CONTAINER_ID}
            />
          </div>
        </div>
        <StatsCardsGroup groups={groups} loading={statsLoading} />
        <div className={ALERTS_HEADER_CLASS}>
          <h3 className={ALERTS_HEADER_TITLE_CLASS}>Alertes déclenchées</h3>
          {filters}
        </div>
        <TriggeredAlertsTable
          activeIssueId={editingIssue?.id || editingIssueId || sharingIssueId || null}
          allFiltered={allFiltered}
          columnOrder={isOrganization ? ORGANIZATION_PRIMARY_COLUMN_ORDER : PRIMARY_COLUMN_ORDER}
          direction={direction}
          error={tableError}
          isIssueUpdating={isUpdating}
          issues={displayedIssues}
          loading={tableLoading}
          pagy={pagy}
          selectedIds={selectedIds}
          sort={sort}
          visibleColumns={visibleColumns}
          onAttributionEdit={handleAttributionEdit}
          onColumnSettingsClick={handleColumnSettingsClick}
          onExportPdf={handleExportPdf}
          onIssueClick={handleIssueClick}
          onPageChange={setPage}
          onPerPageChange={setItems}
          onPhotosClick={handlePhotosClick}
          onShareClick={handleShareClick}
          onSortChange={handleSortChange}
          onStatusEdit={handleStatusEdit}
          onToggleAll={handleToggleAll}
          onToggleRow={handleToggleRow}
        />
      </div>
      {selectedIds.size > 0 || allFiltered ? (
        <SelectionToolbar
          allFiltered={allFiltered}
          buildExportUrl={buildExportUrl}
          selectedIds={exportedIds}
          toastContainerId={TOAST_CONTAINER_ID}
          totalCount={pagy.count}
          onClear={clearSelection}
          onChangeStatus={whenCollect(isOrganization, handleBatchStatusOpen)}
          onSelectAllFiltered={selectAllFiltered}
        />
      ) : null}
      <StatusInlineEditor
        anchorEl={anchorEl}
        currentStatusId={editingIssue?.status_id ?? null}
        isOpen={editorMode === 'status'}
        issueStatuses={editorIssueStatuses}
        onClose={handleEditorClose}
        onSelect={handleStatusSelect}
      />
      <AttributionInlineEditor
        anchorEl={anchorEl}
        availableUsers={editorAvailableUsers}
        currentUserIds={currentUserIds}
        isOpen={editorMode === 'attribution'}
        onApply={handleAttributionApply}
        onClose={handleEditorClose}
      />
      <EditIssueDrawer
        collectId={scopedCollectId(isOrganization, editingIssueCollectId, collectId)}
        initialIssue={restoredIssue}
        isOpen={editingIssueId !== null}
        issueId={editingIssueId}
        onClose={handleEditClose}
        onUpdated={handleIssueUpdated}
      />
      <ShareIssueModal
        collectId={scopedCollectId(isOrganization, sharingIssueCollectId, collectId)}
        isOpen={sharingIssueId !== null}
        issueId={sharingIssueId}
        onClose={handleShareClose}
      />
      {whenCollect(isOrganization, <BatchStatusModal
        collectId={collectId}
        isOpen={isBatchStatusOpen}
        issueStatuses={issueStatuses}
        scope={batchStatusScope}
        onClose={handleBatchStatusClose}
        onUpdated={handleBatchStatusUpdated}
      />)}
      <PhotoViewer
        isOpen={viewerPhotos.length > 0}
        photos={viewerPhotos}
        onClose={handleViewerClose}
      />
      <ColumnSettingsModal
        isOpen={isColumnSettingsOpen}
        optionsLoading={optionsLoading}
        preferences={columnPreferences}
        questionSections={questionSections}
        variables={extraVariables}
        onApply={handleColumnSettingsApply}
        onClose={handleColumnSettingsClose}
        onReset={handleColumnSettingsReset}
      />
    </div>
  )
}

TriggeredAlertsTab.propTypes = {
  collectId: PropTypes.number,
  draftId: PropTypes.number,
  initialColumnPreferences: PropTypes.shape({}),
  isAdmin: PropTypes.bool,
  scope: PropTypes.shape({
    collectIds: PropTypes.arrayOf(PropTypes.number),
    collects: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })),
    kind: PropTypes.oneOf(['collect', 'organization'])
  })
}

TriggeredAlertsTab.defaultProps = {
  collectId: null,
  draftId: null,
  initialColumnPreferences: null,
  isAdmin: false,
  scope: null
}

export default TriggeredAlertsTab
